import { earthquakeContent } from "./records/earthquake.js";
import { tsunamiContent } from "./records/tsunami.js";
import { floodStormContent } from "./records/flood-storm.js";
import { fireEvacuationContent } from "./records/fire-evacuation.js";
import { chemicalsContent } from "./records/chemicals.js";
import { firstAidContent } from "./records/first-aid.js";
import { publicSafetyContent } from "./records/public-safety.js";
import { familyPreparednessContent } from "./records/family-preparedness.js";
import { medicinesContent } from "./records/medicines.js";
import { foodLabelsContent } from "./records/food-labels.js";

import type { Locale } from "../i18n/translations.js";

export interface LearnPracticalSection {
  readonly notice: readonly string[];
  readonly prepare: readonly string[];
  readonly during: readonly string[];
  readonly avoid: readonly string[];
  readonly help: readonly string[];
  readonly example: string;
  /** Topic-specific checks that should be read before pairing food, medicine, or a condition. */
  readonly cautions?: readonly { readonly title: string; readonly text: string }[];
  /** A source-bound household kit that is separate from emergency instructions. */
  readonly emergencyKit?: {
    readonly intro: string;
    readonly target: string;
    readonly items: readonly { readonly title: string; readonly text: string }[];
    readonly rotation: string;
  };
  /** A calm, all-hazards plan for a short interruption to normal services. */
  readonly serviceDisruption?: {
    readonly intro: string;
    readonly actions: readonly { readonly title: string; readonly text: string }[];
    readonly boundary: string;
  };
  readonly situations: readonly { readonly title: string; readonly text: string }[];
  readonly checklist: readonly string[];
}

export type LearnPracticalContent = Record<Locale, LearnPracticalSection>;

export const practicalSectionLabels = {
  th: {
    know: "สิ่งที่ควรรู้",
    now: "เมื่อเกิดเหตุควรทำอะไร",
    what: "เรื่องนี้คืออะไร",
    notice: "สัญญาณหรือสถานการณ์ที่ควรสังเกต",
    prepare: "เตรียมตัวล่วงหน้า",
    during: "เมื่อเกิดเหตุควรทำอะไร",
    avoid: "สิ่งที่ควรหลีกเลี่ยง",
    help: "เมื่อใดควรขอความช่วยเหลือ",
    example: "ตัวอย่างสถานการณ์",
    cautions: "อาหาร ยา และโรค: เช็กก่อนกิน",
    emergencyKit: "ของจำเป็นสำหรับช่วงแรก",
    serviceDisruption: "ความขัดแย้งรุนแรงหรือสงคราม: เตรียมครอบครัว",
    situations: "สถานการณ์ใกล้ตัว",
    checklist: "เช็กลิสต์สั้น ๆ",
    evidence: "แหล่งข้อมูล ขอบเขต และสถานะการตรวจทาน",
  },
  en: {
    know: "What to know",
    now: "What to do when it happens",
    what: "What this topic is",
    notice: "Signs or situations to notice",
    prepare: "Prepare ahead",
    during: "What to do when it happens",
    avoid: "What to avoid",
    help: "When to seek help",
    example: "Example situation",
    cautions: "Food, medicine, and health: check first",
    emergencyKit: "Essentials for the first few days",
    serviceDisruption: "Armed conflict or war: prepare your household",
    situations: "Everyday situations",
    checklist: "Quick checklist",
    evidence: "Sources, scope, and review status",
  },
} as const satisfies Record<Locale, Record<string, string>>;

/** Ten independently stored pre-review records, assembled for offline use. */
export const practicalLearnContent: Readonly<Record<string, LearnPracticalContent>> = {
  earthquake: earthquakeContent,
  tsunami: tsunamiContent,
  "flood-storm": floodStormContent,
  "fire-evacuation": fireEvacuationContent,
  chemicals: chemicalsContent,
  "first-aid": firstAidContent,
  "public-safety": publicSafetyContent,
  "family-preparedness": familyPreparednessContent,
  medicines: medicinesContent,
  "food-labels": foodLabelsContent,
};
